import React, { MouseEvent, useState, useEffect, memo } from "react";
import { Button, Col, Rate, Row, Typography, Card } from "antd";
import { useRouter } from "next/router";
import Image from "next/image";
import MobileCardInfo from "../../page/HomePage/TileContainer/TurfInfoList/MobileCardInfo";
import { showBookNowDynamic } from "../../utils/showFeatures";
import { useCities } from "../../contexts/CitiesContext";
import {
  buildTurfBookingRoute,
  buildTurfDetailRoute,
  getAreaFromPath,
} from "../../utils/turfRoutes";
import {
  getPitchDimensionsLabel,
  getPitchSpecificationLabel,
} from "../../utils/pitchSpecification";
import {
  getPitchSports,
  isSportEnabledAtTurf,
} from "../../utils/sport";

const MobileCard = ({
  name,
  userName,
  imageUrl,
  sportImages,
  activeSport,
  fields,
  data,
  city,
  isUnavailable,
  index,
  isLCP = false,
  distanceLabel = null as string | null,
}: any) => {
  const { cities } = useCities();
  const router = useRouter();
  const [showPackages, setShowPackages] = useState(false);
  const [selectedPitchName, setSelectedPitchName] = useState<string>('');
  const routeArea = getAreaFromPath(router.asPath || "");
  const areaNameForRoute = routeArea || data?.areaName || null;
 
  useEffect(() => {
    if (data?.areaPrice && data.areaPrice.length > 0) {
      setSelectedPitchName(data.areaPrice[0]?.pitchName || '');
    }
    setShowPackages(false);
  }, [data]);

  const { Title, Text } = Typography;

  const req_fields = fields.filter((field: { key: string }) => {
    const contact = data.partnership ? "actualContact" : "displayContact";
    return (
      field.key !== "availabilityData" &&
      field.key !== "isRainProtected" &&
      field.key !== "location" &&
      field.key !== "address" &&
      field.key !== "hasCafe" &&
      field.key !== contact
    );
  });

  const handleCardClick = (userName: string, e: MouseEvent) => {
    e.stopPropagation();
    if (typeof window !== "undefined") {
      sessionStorage.setItem("selectedCardIndex", index);
    }
    if (data?.areaPrice && data.areaPrice.length >= 1) {
      if (!isUnavailable) {
        const url = buildTurfDetailRoute({
          city,
          userName,
          pitchName: data.areaPrice[0].pitchName,
          areaName: areaNameForRoute,
        });
        router.push(activeSport ? `${url}?sport=${activeSport}` : url);
      }
    }
  };

  const handleBookNow = (e: MouseEvent<HTMLElement>, pitchName?: string) => {
    e.stopPropagation();
    // Use the passed pitchName parameter if available, otherwise fall back to selectedPitchName state
    const finalPitchName = pitchName || selectedPitchName;
    const pitch = data?.areaPrice?.find(
      (item: any) => item?.pitchName === finalPitchName
    );
    const sport = activeSport || getPitchSports(pitch)[0];
    if (sport && !isSportEnabledAtTurf(sport, data?.enabledSports)) return;
    if (!isUnavailable && finalPitchName) {
      const bookingUrl = buildTurfBookingRoute({
        city,
        userName,
        pitchName: finalPitchName,
        areaName: areaNameForRoute,
      });
      router.push(activeSport ? `${bookingUrl}?sport=${activeSport}` : bookingUrl);
    }
  };

  const rating = data.starRating ? parseFloat(data.starRating) : 0;
  const imgMeta = ` ${userName} | ${city} | turfbooking`;

  const handleArea = (e: any) => {
    setShowPackages(!showPackages);
  };

  const showPackagesButton = data?.areaPrice && data.areaPrice.length > 1;
  const showBookNowButton = showBookNowDynamic(city, cities) && !showPackagesButton;
  const selectedPitch = data?.areaPrice?.find(
    (pitch: any) => pitch?.pitchName === selectedPitchName
  );
  const selectedCardSport = activeSport || getPitchSports(selectedPitch)[0];
  const isSelectedSportClosed =
    !!selectedCardSport &&
    !isSportEnabledAtTurf(selectedCardSport, data?.enabledSports);

  const handleShowPackages = (e: MouseEvent) => {
    e.stopPropagation();
    setShowPackages(!showPackages);
  };

  const handleTurfCardClick = (index: number) => {
    const selectedPackage = data?.areaPrice?.[index];
    if (selectedPackage) {
      const url = buildTurfDetailRoute({
        city,
        userName,
        pitchName: selectedPackage.pitchName,
        areaName: areaNameForRoute,
      });
      router.push(url);
    }
  };

  return (
    <Row
      className={`card-container-mobile ${isUnavailable ? "unavailable" : ""}`}
      gutter={[8, 8]}
      onClick={(e) => !showPackages && handleCardClick(userName, e)}
    >
      {isUnavailable && <div className="under-maintain">Under Maintenance</div>}

      <Col xs={24} sm={8}>
        <div style={{ position: "relative", width: "100%", height: "210px" }}>
          {distanceLabel ? (
            <span
              className="turf-card-distance-pill turf-card-distance-pill--mobile"
              role="status"
            >
              {distanceLabel}
            </span>
          ) : null}
          <Image
            src={(activeSport && sportImages?.[activeSport]?.length ? sportImages[activeSport] : imageUrl)[0]}
            alt={imgMeta}
            title={imgMeta}
            fill
            quality={95}
            sizes="(max-width: 768px) 100vw, 33vw"
            style={{ objectFit: 'cover' }}
            priority={isLCP}
            loading={isLCP ? 'eager' : 'lazy'}
            {...(isLCP && { fetchPriority: 'high' as const })}
          />
        </div>
        <button
          type="button"
          title={`View more photos of ${name}`}
          aria-label={`View more photos of ${name}`}
          className="more-photos"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            background: 'none',
            border: 'none',
            color: '#0958d9',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          Photos
        </button>
      </Col>
      <Col xs={24} sm={16}>
        <Row
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            display: "flex",
          }}
        >
          <Rate
            style={{
              fontSize: 12,
            }}
            value={rating}
            allowHalf
            disabled
          />
          {data.partnership && (
            <div className="partnership-flag">Partnered</div>
          )}
        </Row>
        <Title ellipsis level={3} className="mt-0 mb-0" style={{ fontSize: '16px', marginBottom: 0 }}>
          {name}
        </Title>
        {!showPackages && (
          <MobileCardInfo
            fields={req_fields}
            data={data}
            name={name}
            rating={rating}
            handleArea={handleArea}
            userName={userName}
            city={city}
          />
        )}
      </Col>
      {showBookNowButton && (
        <Col xs={24}>
          <Button
            type="primary"
            block={true}
            size="large"
            onClick={(e) => handleBookNow(e, selectedPitchName)}
            disabled={isUnavailable || isSelectedSportClosed}
          >
            {isSelectedSportClosed ? "Currently Closed" : "Check Availability"}
          </Button>
        </Col>
      )}
      {showPackages && (
        <Col xs={24}>
          <div className="packages-container">
            {data?.areaPrice?.map((el: any, i: number) => {
              const dimensions = getPitchDimensionsLabel(el);
              const pitchSport = activeSport || getPitchSports(el)[0];
              const isPitchSportClosed =
                !!pitchSport &&
                !isSportEnabledAtTurf(pitchSport, data?.enabledSports);
              return (
              <div
                key={i}
                onClick={(e) => {
                  setSelectedPitchName(el?.pitchName || '')
                  e.stopPropagation(); 
                  handleTurfCardClick(i);
                }}
              >
                <Card bordered>
                  <Text strong style={{ display: 'block', fontSize: '14px' }}>
                    Pitch available - {getPitchSpecificationLabel(el, i)}
                  </Text>
                  {dimensions && (
                    <Text strong style={{ display: 'block', fontSize: '14px' }}>
                      Dimensions - {dimensions}
                    </Text>
                  )}
                  <div className="area-container">
                    <Text strong style={{ fontSize: '14px' }}>
                      Starting @ {el?.price?.weekday?.regularHours || 0}/-
                    </Text>
                    {showBookNowDynamic(city, cities) && (
                      <Button
                        type="primary"
                        block={true}      
                        disabled={isUnavailable || isPitchSportClosed}
                                              onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedPitchName(el?.pitchName || '');
                        handleBookNow(e, el?.pitchName || '');
                      }}
                      >
                        {isPitchSportClosed ? "Currently Closed" : "Check Availability"}
                      </Button>
                    )}
                  </div>
                </Card>
              </div>
              );
            })}
          </div>
        </Col>
      )}
      {showPackagesButton && (
        <>
          {!showPackages ? (
            <Col xs={24}>
              <Button
                block
                type="primary"
                size="large"
                onClick={handleShowPackages}
                disabled={isUnavailable}
                style={{ marginTop: "12px" }}
              >
                Show Pitches ({data?.areaPrice?.length || 0})
              </Button>
            </Col>
          ) : (
            <Col xs={24}>
              <Button
                type="link"
                onClick={handleShowPackages}
                style={{
                  display: "block",
                  color: "#0958d9",
                  textAlign: "center",
                  fontSize: "1.3em",
                  paddingLeft: "80px",
                }}
                disabled={isUnavailable}
                className="back-to-overview"
                aria-label="Go back to turf overview"
              >
                <span className="back-to-overview-link">
                  {" "}
                  &lt; back to overview{" "}
                </span>
              </Button>
            </Col>
          )}
        </>
      )}
    </Row>
  );
};

// OPTIMIZED: Memoize the MobileCard component to prevent unnecessary re-renders
const MemoizedMobileCard = memo(MobileCard, (prevProps, nextProps) => {
  return (
    prevProps.userName === nextProps.userName &&
    prevProps.city === nextProps.city &&
    prevProps.index === nextProps.index &&
    prevProps.isUnavailable === nextProps.isUnavailable &&
    prevProps.name === nextProps.name &&
    prevProps.distanceLabel === nextProps.distanceLabel &&
    prevProps.data?.starRating === nextProps.data?.starRating &&
    prevProps.data?.partnership === nextProps.data?.partnership &&
    JSON.stringify(prevProps.data?.enabledSports) === JSON.stringify(nextProps.data?.enabledSports) &&
    JSON.stringify(prevProps.data?.areaPrice) === JSON.stringify(nextProps.data?.areaPrice)
  );
});

export default MemoizedMobileCard;